/**
 * ONE FETCH PER SIGNED-IN SESSION, not one per family surface (packet J8
 * round 2, Codex PR #1105 review).
 *
 * PR #1105 review (Codex, on this exact packet): the first cut of packet
 * J8 threaded the server answer through useDancerClassSet only, whose sole
 * caller is FamilyDancerProfile.tsx - FamilyClasses.tsx, FamilyCalendar.tsx
 * and FamilyCommandCenter.tsx kept calling familyClassSet directly and so
 * kept the bug. Closing that meant every one of those surfaces needs this
 * payload, and each mounting its OWN fetch would have turned one request a
 * session into three or four for the same signed-in family, right after the
 * read-budget incident (docs/handoff, issue #1025) that this exact PR
 * measured its own cost against.
 *
 * So the fetch runs at most ONCE per session, held in this module-level
 * cache keyed by uid. Every caller of useFamilyStaffRolesPayload
 * (lib/familyView.ts) reads the SAME snapshot through React's
 * useSyncExternalStore.
 *
 * WHAT INVALIDATES IT:
 *   - a different uid (a sign-out followed by a different sign-in reads as
 *     a fresh cache automatically, uid-scoped exactly the way
 *     lib/previewRole.ts's stored preview is uid-scoped, for the same
 *     shared-device reason);
 *   - lib/auth.ts's signOut() also calls resetFamilyStaffRolesCache()
 *     directly, belt and braces, matching that function's own
 *     setPreviewRole(null) and clearLastHomeShell() calls;
 *   - the caller's own dancer list growing past what the cached payload
 *     named (a dancer Kim just linked, most likely) - see
 *     ensureFamilyStaffRolesCached's own header for how that is detected
 *     without a second fetch to check.
 *
 * NO PII IN THE SNAPSHOT beyond exactly what the server already scoped to
 * this caller: offering ids only, per dancer id. Nothing here widens what a
 * session can see - it is a cache of one authenticated read, not a new one.
 *
 * PURE ON PURPOSE, same reason lib/jrStaffAliases.ts and
 * lib/familyClassSet.ts give: this file imports nothing from Firebase, not
 * even indirectly. `DancerStaffRoles` is pulled in as `import type` only,
 * which both tsc and node's type stripping erase completely - proven by
 * running it (tests/family-staff-roles-cache.test.mjs loads this module
 * directly and lib/familyStaffRoles.ts, which imports firebase/auth and
 * ./functionSecurity.ts, is never touched). The actual network call is
 * handed in as `fetcher` by the caller (lib/familyView.ts's
 * useFamilyStaffRolesPayload, which passes fetchFamilyStaffRoles) instead
 * of imported here - the one thing that keeps this module Firebase-free
 * while still doing real caching.
 */
import type { DancerStaffRoles } from './familyStaffRoles.ts';

export type FamilyStaffRolesCacheStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface FamilyStaffRolesCacheSnapshot {
  uid: string | null;
  status: FamilyStaffRolesCacheStatus;
  payload: Map<string, DancerStaffRoles> | null;
  error: string | null;
}

const IDLE_SNAPSHOT: FamilyStaffRolesCacheSnapshot = {
  uid: null, status: 'idle', payload: null, error: null,
};

let cacheSnapshot: FamilyStaffRolesCacheSnapshot = IDLE_SNAPSHOT;
const cacheListeners = new Set<() => void>();
// A generation counter, not a uid comparison alone, guards a slow response
// arriving after a FASTER, later call already replaced it - two sign-ins in
// quick succession on a shared device must never let the first account's
// slow network response land on top of the second account's fresh fetch,
// even though both would carry different uids and a same-uid check alone
// would miss the case of the SAME uid signing out and back in twice.
let cacheGeneration = 0;

function setCacheSnapshot(next: FamilyStaffRolesCacheSnapshot): void {
  cacheSnapshot = next;
  for (const listener of cacheListeners) listener();
}

/** useSyncExternalStore's subscribe half. */
export function subscribeFamilyStaffRolesCache(listener: () => void): () => void {
  cacheListeners.add(listener);
  return () => {
    cacheListeners.delete(listener);
  };
}

/** useSyncExternalStore's getSnapshot half. Same reference until something changes. */
export function getFamilyStaffRolesCacheSnapshot(): FamilyStaffRolesCacheSnapshot {
  return cacheSnapshot;
}

/**
 * Start (or reuse) the ONE fetch this session needs. A call while a fetch
 * for this exact uid is already in flight, or while a READY payload for
 * this uid already names every id in `knownDancerIds`, does nothing -
 * that is what keeps this to one network call per session rather than one
 * per mounted surface.
 *
 * `knownDancerIds` is the live signal that invalidates a stale cache: when
 * a dancer this family now has is NOT a key the cached payload named, the
 * cache predates her (Kim just linked a new dancer, most likely) and is
 * refetched. Fewer ids than the payload names is not itself a reason to
 * refetch - the payload simply knows about a dancer this particular render
 * has not loaded yet, which is harmless.
 *
 * `fetcher` is called at most once per actual fetch this triggers; the
 * caller supplies it (fetchFamilyStaffRoles bound to the signed-in user, in
 * production) so this module never has to import anything Firebase-shaped
 * itself.
 */
export function ensureFamilyStaffRolesCached(
  uid: string,
  knownDancerIds: readonly string[],
  fetcher: () => Promise<Map<string, DancerStaffRoles>>
): void {
  if (cacheSnapshot.uid !== uid) {
    cacheSnapshot = IDLE_SNAPSHOT;
  }
  const stale = cacheSnapshot.status === 'ready' && cacheSnapshot.payload
    ? knownDancerIds.some((id) => !cacheSnapshot.payload!.has(id))
    : false;
  if (cacheSnapshot.uid === uid
      && (cacheSnapshot.status === 'loading' || (cacheSnapshot.status === 'ready' && !stale))) {
    return;
  }
  const myGeneration = (cacheGeneration += 1);
  setCacheSnapshot({
    uid,
    status: 'loading',
    payload: cacheSnapshot.uid === uid ? cacheSnapshot.payload : null,
    error: null,
  });
  fetcher()
    .then((result) => {
      if (myGeneration !== cacheGeneration) return;
      setCacheSnapshot({ uid, status: 'ready', payload: result, error: null });
    })
    .catch((caught) => {
      if (myGeneration !== cacheGeneration) return;
      setCacheSnapshot({
        uid,
        status: 'error',
        payload: null,
        error: caught instanceof Error ? caught.message : String(caught),
      });
    });
}

/**
 * Sign-out clears the cache outright. Belt and braces alongside the uid
 * scope above (matching lib/previewRole.ts's setPreviewRole(null) and
 * lib/lastHomeShell.ts's clearLastHomeShell(), both also called directly
 * from lib/auth.ts's signOut()) - the next account on a shared device must
 * never see this render a stale snapshot even for the one tick before its
 * own uid mismatch would have cleared it anyway.
 */
export function resetFamilyStaffRolesCache(): void {
  cacheGeneration += 1;
  setCacheSnapshot(IDLE_SNAPSHOT);
}
